<template>
  <div class="flex h-full relative md:overflow-hidden"
       :class="[{'overflow-hidden' : showSidebar}]"
  >
    <div class="h-full flex max-w-full w-268 anim-position-width flex-shrink-0 absolute"
         :class="[
           showSidebar ? 'left-0' : '-left-268'
         ]"
    >
      <div id="sidebar" class="flex flex-col pb-60 md:pb-0"
           :class="[
             isDarkMode ? 'border-hover' : 'border-grey-500',
             projectInstructionsActiveThirdStep ? 'bg-transparent' : isPaperShape ? 'bg-book w-268 mx-auto' : 'w-full bg-primary border-r'
           ]"
           @click="closeSidebar"
      >
        <div class="overflow-hidden" @click.stop>
          <Projects />
        </div>
      </div>
    </div>

    <div class="h-full relative anim-position-width"
         :class="[
           showSidebar ? 'left-268 w-full md:w-calc-268 ' : 'left-0 w-full',
           isPaperShape ? 'md:pt-60' : '',
         ]"
    >
      <div class="flex justify-end -mb-40 pr-15 pt-10 pb-5 relative z-20"
           :class="[
             {'max-w-1100 mx-auto rounded-t-9 pt-5' : isPaperShape},
             !isProjectInstructionAccepted ? 'bg-transparent' : isDarkMode ? 'bg-canvas' : 'bg-primary',
             {'pt-70': isMobile}
           ]"
      >
        <div id="menu-items" class="flex items-center">
          <Share
            v-if="currentProject"
            id="share-menu"
            :class="{'opacity-0': isFocusMode && isProjectInstructionAccepted}"
            :project="currentProject"
            class="md:mr-2"
          />

          <More v-if="currentProject" :class="{'opacity-0': isFocusMode && isProjectInstructionAccepted}" />
        </div>
      </div>

      <div
        class="h-full relative"
        :class="[
          isMobile ? '' : 'overflow-y-auto',
          !isProjectInstructionAccepted ? '' : isDarkMode ? 'bg-canvas' : 'bg-primary',
          {'max-w-1100 mx-auto rounded-t-9' : isPaperShape},
          {'shadow-paperMode' : isPaperShape && !isDarkMode && isProjectInstructionAccepted},
        ]"
      >
        <Workspace
          v-if="currentProject"
          :key="currentProject.id"
          :project="currentProject"
          :book-id="currentProject.bookId"
          :projects="book.projects"
          :allow-edit="!currentProject.isLocked"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Workspace from '@/components/workspace/Workspace'
import localStorageService from '@/core/local-storage'
import Projects from '@/components/projects/Projects'

import { defineComponent, computed, onBeforeUnmount, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import More from '@/components/header/More'
import Share from '@/components/header/Share'

export default defineComponent({
  name: 'BookView',

  components: { Workspace, Projects, More, Share },

  setup () {
    const { state, dispatch, commit, getters } = useStore()
    const route = useRoute()
    const router = useRouter()

    const currentProject = computed(() => state.project.currentProject)
    const hasTemplates = computed(() => getters.hasTemplates)
    const isTrialOver = computed(() => getters.isTrialOver)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const book = computed(() => state.book.book)
    const booksMemory = computed(() => state.book.booksMemory)
    const bookId = computed(() => route.params.id)
    const projectId = computed(() => route.query.page)
    const team = computed(() => state.team.team)
    const showSidebar = computed(() => state.settings.showSidebar)

    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isPaperShape = computed(() => getters.isPaperShape)
    const isProjectInstructionAccepted = computed(() => getters.isProjectInstructionAccepted)
    const projectInstructionsActiveThirdStep = computed(() => !isProjectInstructionAccepted.value && state.profile.activeStep === 3)
    const isFocusMode = computed(() => state.settings.isFocusMode)

    watch(projectId, (value) => {
      if (value) {
        dispatch('getProject', projectId.value).then(res => {
          document.title = res.title + ' - hello ivy'
          localStorageService.setItem('lastOpenedProject', { bookId: bookId.value, projectId: projectId.value })
        })
      }
    })

    watch(bookId, () => getBookById())

    getBookById()

    if (!hasTemplates.value) {
      dispatch('getTemplates')
    }

    onMounted(() => {
      if (isTeamAccount.value && (isTrialOver.value || team.value.link !== route.params.team)) {
        router.push({ name: 'TeamBooks', params: { team: team.value.link } })
      }
    })

    onBeforeUnmount(() => {
      if (book.value?.id && currentProject.value?.id) {
        commit('setBooksMemory', {
          bookId: book.value.id,
          projectId: currentProject.value.id
        })
      }
      commit('clearProject')
    })

    function getBookById () {
      commit('toggleBookLoading', true)

      if (!bookId.value) {
        commit('toggleBookLoading', false)
        return
      }

      dispatch('getBook', bookId.value).then(res => {
        commit('toggleBookLoading', false)

        if (isTeamAccount.value && res.id && res.chat?.id) {
          dispatch('getMessages', {
            bookId: res.id,
            chatId: res.chat.id
          })
        }

        if (route.query.page) {
          dispatch('getSize', route.query.page)
          dispatch('getShare', route.query.page)

          const project = res.projects.find(item => item.id === +route.query.page)
          if (project) {
            document.title = project.title + ' - hello ivy'
            localStorageService.setItem('lastOpenedProject', { bookId: bookId.value, projectId: route.query.page })
            commit('setCurrentProject', project)
          } else {
            router.replace({ query: { ...route.query, page: res.projects[0]?.id } })
            dispatch('getShare', res.projects[0]?.id)
          }
        } else {
          const query = res?.id && booksMemory.value[res?.id] ? booksMemory.value[res?.id] : res.projects[0]?.id
          dispatch('getShare', query)

          router.replace({ query: { ...route.query, page: query } })
        }
      })
    }

    function closeSidebar () {
      if (isMobile.value) commit('updateSidebar', false)
    }

    return {
      // computed
      book,
      isMobile,
      isDarkMode,
      showSidebar,
      isFocusMode,
      isPaperShape,
      currentProject,
      isProjectInstructionAccepted,
      projectInstructionsActiveThirdStep,
      // function
      closeSidebar
    }
  }
})
</script>
